import { Component, OnInit, OnDestroy } from '@angular/core'
import { Functions, httpsCallable } from '@angular/fire/functions' // Modern Firebase SDK
import { AuthenticationService } from '../../../services/authentication.service'
import { GlobalService } from '../../../services/global.service'
import { ToastrService } from 'ngx-toastr'

interface ConsoleError {
    message: string
    timestamp: Date
    type: 'error' | 'warn'
}

@Component({
    selector: 'app-floating-help-button',
    templateUrl: './floating-help-button.component.html',
    styleUrls: ['./floating-help-button.component.scss'],
})
export class FloatingHelpButtonComponent implements OnInit, OnDestroy {
    isMenuOpen = false
    isReporting = false
    consoleErrors: ConsoleError[] = []
    private originalConsoleError: any
    private originalConsoleWarn: any

    // WhatsApp config
    whatsappNumber = '27761340551'
    whatsappMessage = 'Hello, I have a question.'

    // Store user email from observable
    private userEmail: string | null = null

    constructor(private functions: Functions, private authService: AuthenticationService, public globalService: GlobalService, private toastr: ToastrService) {}

    ngOnInit(): void {
        // Console capture disabled - it interferes with error source tracing in dev tools
        // To re-enable, uncomment the line below:
        // this.setupConsoleCapture()

        // Subscribe to auth state to get user email
        this.authService.authenticatedUser$.subscribe((user) => {
            this.userEmail = user?.email || null
        })
    }

    ngOnDestroy(): void {
        // Restore original console methods
        if (this.originalConsoleError) {
            console.error = this.originalConsoleError
        }
        if (this.originalConsoleWarn) {
            console.warn = this.originalConsoleWarn
        }
    }

    private setupConsoleCapture(): void {
        // Store original console methods
        this.originalConsoleError = console.error
        this.originalConsoleWarn = console.warn

        // Override console.error
        console.error = (...args: any[]) => {
            this.captureError('error', args)
            this.originalConsoleError.apply(console, args)
        }

        // Override console.warn
        console.warn = (...args: any[]) => {
            this.captureError('warn', args)
            this.originalConsoleWarn.apply(console, args)
        }

        // Capture unhandled errors
        window.addEventListener('error', (event) => {
            this.consoleErrors.push({
                message: `Unhandled Error: ${event.message} at ${event.filename}:${event.lineno}:${event.colno}`,
                timestamp: new Date(),
                type: 'error',
            })
            this.trimErrors()
        })

        // Capture unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            this.consoleErrors.push({
                message: `Unhandled Promise Rejection: ${event.reason}`,
                timestamp: new Date(),
                type: 'error',
            })
            this.trimErrors()
        })
    }

    private captureError(type: 'error' | 'warn', args: any[]): void {
        const message = args.map((arg) => (typeof arg === 'object' ? JSON.stringify(arg) : String(arg))).join(' ')

        this.consoleErrors.push({
            message: message.substring(0, 500), // Limit message length
            timestamp: new Date(),
            type,
        })

        this.trimErrors()
    }

    private trimErrors(): void {
        // Keep only last 20 errors
        if (this.consoleErrors.length > 20) {
            this.consoleErrors = this.consoleErrors.slice(-20)
        }
    }

    toggleMenu(): void {
        this.isMenuOpen = !this.isMenuOpen
    }

    closeMenu(): void {
        this.isMenuOpen = false
    }

    openWhatsApp(): void {
        const encodedMessage = encodeURIComponent(this.whatsappMessage)
        const url = `https://api.whatsapp.com/send?phone=${this.whatsappNumber}&text=${encodedMessage}`
        window.open(url, '_blank')
        this.closeMenu()
    }

    async reportProblem(): Promise<void> {
        this.isReporting = true

        try {
            // Collect diagnostic information
            const diagnosticData = await this.collectDiagnostics()

            // Call Firebase callable function (no URL/rewrite needed!)
            const callable = httpsCallable(this.functions, 'reportProblem')
            await callable(diagnosticData)

            this.toastr.success('Problem report sent successfully. Our team will investigate.', 'Thank you!')
            this.closeMenu()
        } catch (error) {
            console.error('Failed to send problem report:', error)
            this.toastr.error('Failed to send report. Please try WhatsApp instead.', 'Error')
        } finally {
            this.isReporting = false
        }
    }

    private async collectDiagnostics(): Promise<any> {
        // Get public IP
        let publicIP = 'unknown'
        try {
            const ipResponse = await GlobalService.getPublicIP()
            publicIP = ipResponse?.ip || 'unknown'
        } catch (e) {
            publicIP = 'fetch-failed'
        }

        // Get performance metrics
        let performanceMetrics: any = {}
        if (window.performance) {
            const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
            if (navigation) {
                performanceMetrics = {
                    pageLoadTime: navigation.loadEventEnd - navigation.startTime,
                    domContentLoaded: navigation.domContentLoadedEventEnd - navigation.startTime,
                    firstByte: navigation.responseStart - navigation.requestStart,
                }
            }
        }

        // Get memory info (Chrome only)
        let memoryInfo: any = null
        if ((performance as any).memory) {
            const mem = (performance as any).memory
            memoryInfo = {
                usedJSHeapSize: Math.round(mem.usedJSHeapSize / 1048576) + ' MB',
                totalJSHeapSize: Math.round(mem.totalJSHeapSize / 1048576) + ' MB',
            }
        }

        return {
            // User info
            userId: this.authService.uid || 'anonymous',
            userEmail: this.userEmail || 'unknown',
            isAnonymous: this.authService.isAnonymous,

            // Environment
            url: window.location.href,
            referrer: document.referrer || 'direct',
            timestamp: new Date().toISOString(),

            // Browser info
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language,
            cookiesEnabled: navigator.cookieEnabled,
            onLine: navigator.onLine,

            // Screen info
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
            devicePixelRatio: window.devicePixelRatio,

            // Network
            publicIP,

            // Performance
            performanceMetrics,
            memoryInfo,

            // Console errors (last 20)
            consoleErrors: this.consoleErrors.map((e) => ({
                message: e.message,
                timestamp: e.timestamp.toISOString(),
                type: e.type,
            })),

            // Enterprise context
            enterprise: this.globalService.enterpriseConfig?.key || 'parkupp',
            subdomain: this.globalService.subdomain || 'www',

            // Local storage keys (for debugging state issues)
            localStorageKeys: Object.keys(localStorage),

            // Session storage keys
            sessionStorageKeys: Object.keys(sessionStorage),
        }
    }

    // Check if button should be visible
    get isVisible(): boolean {
        // Show for ParkUpp, hide for enterprises unless configured
        if (this.globalService.isEnterprise) {
            return this.globalService.enterpriseConfig?.showWhatsAppButton ?? false
        }
        return true
    }
}
